import { NgModule } from '@angular/core';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { DialogModule } from '@angular/cdk/dialog';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { CardSoftwareLibraryModule, LOGIN_AUTH_SERVICE, LOGIN_CONFIG } from '@card-software/library';
import { CommonModule, DATE_PIPE_DEFAULT_OPTIONS, DecimalPipe } from '@angular/common';
import { UserAuthenticationService } from './services/user-authentication.service';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { providePrimeNG } from 'primeng/config';
import { PrimengModule } from './primeng/primeng.module';
import { ConfirmationService, MessageService } from 'primeng/api';
import { siteInterceptor, errorInterceptor, SITE_API_BASE_URL } from '@card-software/nexus-utilities';
import { authInterceptor } from './interceptors';
import { environment } from '../environments/environment';
import { BrowserModule } from '@angular/platform-browser';
import { ScrollingModule } from '@angular/cdk/scrolling';
import { RepresentativesViewerComponent } from './components/shared/representatives-viewer/representatives-viewer.component';
import { RepresentativeDetailsModalComponent } from './components/shared/modals/representative-details-modal/representative-details-modal.component';
import { AddressesViewerComponent } from './components/shared/addresses-viewer/addresses-viewer.component';
import { AddressDetailModalComponent } from './components/shared/modals/address-detail-modal.component/address-detail-modal.component';
import { LotsIdModalComponent } from './components/shared/modals/lots-id-modal/lots-id-modal.component';
import { ApDocumentQuickViewModalComponent } from './components/shared/modals/ap-document-quick-view-modal/ap-document-quick-view-modal.component';
import { ReceivedCoverageModalComponent } from './components/accounts-payable/_modals/received-coverage-modal/received-coverage-modal.component';
import { ReceivedCoverageTagComponent } from './components/shared/received-coverage-tag/received-coverage-tag.component';
import { ReceivedCoverageBannerComponent } from './components/shared/received-coverage-banner/received-coverage-banner.component';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { CustomTranslateLoader } from './services/custom.translate.loader';
import { SierraTheme } from '@card-software/library';
import { BusinessesComponent } from './components/business/businesses/businesses.component';
import { InfoComponent } from './components/info/info.component';
import { BusinessDetailsComponent } from './components/business/business-details/business-details.component';
import { TabsModule } from 'primeng/tabs';
import { ProviderDetailsComponent } from './components/business/provider-details/provider-details.component';
import { ClientDetailsViewerComponent } from './components/shared/client-details-viewer/client-details-viewer.component';
import { TranslateWithFallbackPipe } from './pipes/translate-with-fallback.pipe';
import { MaskedAmountPipe } from './pipes/masked-amount.pipe';
import { ExchangeRateComponent } from './components/exchange-rate/exchange-rate.component';
import { UnauthorizedComponent } from './components/unauthorized/unauthorized.component';
import { TranslateLocalizedPipe } from './pipes/translate-localized.pipe';
import { CostAnalysisPageComponent } from './components/cost-analysis/cost-analysis-page/cost-analysis-page.component';
import { FabricSummaryComponent } from './components/cost-analysis/fabric-summary/fabric-summary.component';
import { ProcessSummaryComponent } from './components/cost-analysis/process-summary/process-summary.component';
import { ComponentSummaryComponent } from './components/cost-analysis/component-summary/component-summary.component';
import { ProcessDetailPageComponent } from './components/cost-analysis/process-detail-page/process-detail-page.component';
import { CalculationControlComponent } from './components/cost-analysis/calculation-control/calculation-control.component';
import { PoolControlComponent } from './components/costs/pools/pool-control/pool-control.component';
import { PoolsPageComponent } from './components/costs/pools/pools-page.component';
import { ReportsPageComponent } from './components/costs/reports/reports-page.component';
import { PoolReadinessBannerComponent } from './components/costs/pools/pool-readiness-banner/pool-readiness-banner.component';
import { CostKpiCardComponent } from './components/cost-analysis/shared/cost-kpi-card/cost-kpi-card.component';
import { CostModeChipComponent } from './components/cost-analysis/shared/cost-mode-chip/cost-mode-chip.component';
import { BankAccountComponent } from './components/bank-accounts/bank-account/bank-account.component';
import { BankAccountDetailsComponent } from './components/bank-accounts/bank-account-details/bank-account-details.component';
import { BankTransactionComponent } from './components/bank-transactions/bank-transaction/bank-transaction.component';
import { BankTransactionDetailsComponent } from './components/bank-transactions/bank-transaction-details/bank-transaction-details.component';
import { InternalTransfersComponent } from './components/internal-transfers/internal-transfers/internal-transfers.component';
import { InternalTransferDetailsComponent } from './components/internal-transfers/internal-transfer-details/internal-transfer-details.component';
import { BankStatementComponent } from './components/bank-statements/bank-statement/bank-statement.component';
import { BankReconciliationComponent } from './components/bank-reconciliation/bank-reconciliation/bank-reconciliation.component';
import { EmployeeCostsTabComponent } from './components/monthly-uploads/employee-costs-tab/employee-costs-tab.component';
import { AssetDepreciationTabComponent } from './components/monthly-uploads/asset-depreciation-tab/asset-depreciation-tab.component';
import { ServiceCostsTabComponent } from './components/monthly-uploads/service-costs-tab/service-costs-tab.component';
import { MonthlyUploadsComponent } from './components/monthly-uploads/monthly-uploads.component';
import { ApAgingComponent } from './components/accounts-payable/ap-aging/ap-aging.component';
import { ApDocumentsComponent } from './components/accounts-payable/ap-documents/ap-documents.component';
import { ApDocumentDetailsComponent } from './components/accounts-payable/ap-document-details/ap-document-details.component';
import { ApWithholdingDetailsComponent } from './components/accounts-payable/ap-withholding-details/ap-withholding-details.component';
import { ApWithholdingsComponent } from './components/accounts-payable/ap-withholdings/ap-withholdings.component';
import { WithholdingRemittanceComponent } from './components/accounts-payable/withholding-remittance/withholding-remittance.component';
import { ApPaymentsComponent } from './components/accounts-payable/ap-payments/ap-payments.component';
import { ApPaymentDetailsComponent } from './components/accounts-payable/ap-payment-details/ap-payment-details.component';
import { ApPaymentRequestsComponent } from './components/accounts-payable/ap-payment-requests/ap-payment-requests.component';
import { ApPaymentRequestDetailsComponent } from './components/accounts-payable/ap-payment-request-details/ap-payment-request-details.component';
import { PoSettlementComponent } from './components/accounts-payable/po-settlement/po-settlement.component';
import { PoSettlementDetailsComponent } from './components/accounts-payable/po-settlement/po-settlement-details.component';
import { ApAdvanceRequestsComponent } from './components/accounts-payable/ap-advance-requests/ap-advance-requests.component';
import { ApAdvanceRequestDetailsComponent } from './components/accounts-payable/ap-advance-request-details/ap-advance-request-details.component';
import { ApAdvancesComponent } from './components/accounts-payable/ap-advances/ap-advances.component';
import { ApAdvanceDetailsComponent } from './components/accounts-payable/ap-advance-details/ap-advance-details.component';
import { FinancialAccountSelectComponent } from './components/shared/financial-account-select/financial-account-select.component';
import { TaxAuthorizationsComponent } from './components/tax-authorizations/tax-authorizations.component';
import { TaxAuthorizationsViewerComponent } from './components/shared/tax-authorizations-viewer/tax-authorizations-viewer.component';
import { TaxAuthorizationDetailsModalComponent } from './components/tax-authorizations/tax-authorization-details-modal/tax-authorization-details-modal.component';
import { CashFundingDetailsComponent } from './components/cash-funding/cash-funding-details/cash-funding-details.component';
import { CashFundingsComponent } from './components/cash-funding/cash-fundings/cash-fundings.component';
import { CashRequestsComponent } from './components/cash-request/cash-requests/cash-requests.component';
import { CashRequestDetailsComponent } from './components/cash-request/cash-request-details/cash-request-details.component';
import { CashRequestDocumentPickerModalComponent } from './components/cash-request/_modals/cash-request-document-picker-modal/cash-request-document-picker-modal.component';
import { CashRequestLineModalComponent } from './components/cash-request/_modals/cash-request-line-modal/cash-request-line-modal.component';
import { CashRequestBreakdownComponent } from './components/shared/cash-request-breakdown/cash-request-breakdown.component';
import { DocumentIdentityComponent } from './components/shared/document-identity/document-identity.component';
import { BankChargesSectionComponent } from './components/shared/bank-charges-section/bank-charges-section.component';
import { BankMovementFormComponent } from './components/shared/bank-movement-form/bank-movement-form.component';
import { SettlementSummaryComponent } from './components/shared/settlement-summary/settlement-summary.component';
import { PayableDocumentSummaryComponent } from './components/shared/payable-document-summary/payable-document-summary.component';
import { JournalEntrySummaryComponent } from './components/shared/journal-entry-summary/journal-entry-summary.component';
import { ChartOfAccountsComponent } from './components/ledger/chart-of-accounts/chart-of-accounts.component';
import { JournalEntriesComponent } from './components/ledger/journal-entries/journal-entries.component';
import { JournalEntryDetailsComponent } from './components/ledger/journal-entry-details/journal-entry-details.component';
import { JournalEntryManualComponent } from './components/ledger/journal-entry-manual/journal-entry-manual.component';
import { AccountingPeriodsComponent } from './components/ledger/accounting-periods/accounting-periods.component';
import { WithholdingTaxesComponent } from './components/withholding-taxes/withholding-taxes.component';
import { AccountSubledgerComponent } from './components/ledger/account-subledger/account-subledger.component';
import { AccountCreateModalComponent } from './components/ledger/_modals/account-create-modal/account-create-modal.component';
import { AccountEditModalComponent } from './components/ledger/_modals/account-edit-modal/account-edit-modal.component';
import { ExpenseConceptsComponent } from './components/expense-concepts/expense-concepts.component';
import { ExpenseConceptModalComponent } from './components/expense-concepts/_modals/expense-concept-modal/expense-concept-modal.component';
import { EmployeesComponent } from './components/business/employees/employees.component';
import { EmployeeDetailsComponent } from './components/business/employee-details/employee-details.component';
import { AddEmployeeModalComponent } from './components/business/employees/_modals/add-employee-modal/add-employee-modal.component';
import { PettyCashFundsComponent } from './components/petty-cash/funds/funds.component';
import { PettyCashFundDetailsComponent } from './components/petty-cash/fund-details/fund-details.component';
import { FundOpeningComponent } from './components/petty-cash/fund-opening/fund-opening.component';
import { TripsComponent } from './components/petty-cash/trips/trips.component';
import { TripDetailsComponent } from './components/petty-cash/trip-details/trip-details.component';
import { CostCenterSelectComponent } from './components/shared/cost-center-select/cost-center-select.component';
import { ReceiptCaptureComponent } from './components/petty-cash/receipt-capture/receipt-capture.component';
import { ExpenseClaimsComponent } from './components/petty-cash/expense-claims/expense-claims.component';
import { ExpenseClaimDetailsComponent } from './components/petty-cash/expense-claim-details/expense-claim-details.component';
import { OwnerClaimsComponent } from './components/petty-cash/owner-claims/owner-claims.component';
import { ExpenseSettlementsComponent } from './components/petty-cash/expense-settlements/expense-settlements.component';
import { ExpenseSettlementDetailsComponent } from './components/petty-cash/expense-settlement-details/expense-settlement-details.component';
import { EmployeeAdvancesComponent } from './components/petty-cash/employee-advances/employee-advances.component';
import { EmployeeAdvanceDetailsComponent } from './components/petty-cash/employee-advance-details/employee-advance-details.component';
import { OwnerSettlementsComponent } from './components/petty-cash/owner-settlements/owner-settlements.component';
import { TripAdvancesComponent } from './components/petty-cash/trip-advances/trip-advances.component';
import { ExpenseReceiptEditorComponent } from './components/petty-cash/expense-receipt-editor/expense-receipt-editor.component';
import { CostCentersComponent } from './components/cost-centers/cost-centers.component';
import { GlAccountRulesComponent } from './components/gl-account-rules/gl-account-rules.component';
import { GlAccountRuleDrawerComponent } from './components/gl-account-rules/gl-account-rule-drawer/gl-account-rule-drawer.component';
import { PartyStatementComponent } from './components/ledger/party-statement/party-statement.component';

@NgModule({
  declarations: [
    AppComponent,
    RepresentativesViewerComponent,
    RepresentativeDetailsModalComponent,
    AddressesViewerComponent,
    AddressDetailModalComponent,
    LotsIdModalComponent,
    ApDocumentQuickViewModalComponent,
    ReceivedCoverageModalComponent,
    ReceivedCoverageTagComponent,
    ReceivedCoverageBannerComponent,
    BusinessesComponent,
    InfoComponent,
    BusinessDetailsComponent,
    ProviderDetailsComponent,
    ClientDetailsViewerComponent,
    ExchangeRateComponent,
    UnauthorizedComponent,
    CostAnalysisPageComponent,
    FabricSummaryComponent,
    ProcessSummaryComponent,
    ComponentSummaryComponent,
    ProcessDetailPageComponent,
    CalculationControlComponent,
    PoolControlComponent,
    PoolsPageComponent,
    ReportsPageComponent,
    PoolReadinessBannerComponent,
    CostKpiCardComponent,
    CostModeChipComponent,
    BankAccountComponent,
    BankAccountDetailsComponent,
    BankTransactionComponent,
    BankTransactionDetailsComponent,
    BankStatementComponent,
    BankReconciliationComponent,
    EmployeeCostsTabComponent,
    AssetDepreciationTabComponent,
    ServiceCostsTabComponent,
    MonthlyUploadsComponent,
    ApDocumentsComponent,
    ApAgingComponent,
    ApDocumentDetailsComponent,
    ApWithholdingDetailsComponent,
    ApWithholdingsComponent,
    WithholdingRemittanceComponent,
    ApPaymentsComponent,
    ApPaymentDetailsComponent,
    ApPaymentRequestsComponent,
    ApPaymentRequestDetailsComponent,
    PoSettlementComponent,
    PoSettlementDetailsComponent,
    ApAdvanceRequestsComponent,
    ApAdvanceRequestDetailsComponent,
    ApAdvancesComponent,
    ApAdvanceDetailsComponent,
    FinancialAccountSelectComponent,
    TaxAuthorizationsComponent,
    TaxAuthorizationsViewerComponent,
    TaxAuthorizationDetailsModalComponent,
    CashFundingDetailsComponent,
    CashFundingsComponent,
    CashRequestsComponent,
    CashRequestDetailsComponent,
    CashRequestDocumentPickerModalComponent,
    CashRequestLineModalComponent,
    CashRequestBreakdownComponent,
    DocumentIdentityComponent,
    BankChargesSectionComponent,
    BankMovementFormComponent,
    InternalTransfersComponent,
    InternalTransferDetailsComponent,
    SettlementSummaryComponent,
    PayableDocumentSummaryComponent,
    JournalEntrySummaryComponent,
    ChartOfAccountsComponent,
    JournalEntriesComponent,
    JournalEntryDetailsComponent,
    JournalEntryManualComponent,
    AccountingPeriodsComponent,
    WithholdingTaxesComponent,
    AccountCreateModalComponent,
    AccountEditModalComponent,
    ExpenseConceptsComponent,
    ExpenseConceptModalComponent,
    EmployeesComponent,
    EmployeeDetailsComponent,
    AddEmployeeModalComponent,
    PettyCashFundsComponent,
    PettyCashFundDetailsComponent,
    FundOpeningComponent,
    TripsComponent,
    TripDetailsComponent,
    CostCenterSelectComponent,
    ReceiptCaptureComponent,
    ExpenseClaimsComponent,
    ExpenseClaimDetailsComponent,
    OwnerClaimsComponent,
    ExpenseSettlementsComponent,
    ExpenseSettlementDetailsComponent,
    EmployeeAdvancesComponent,
    EmployeeAdvanceDetailsComponent,
    OwnerSettlementsComponent,
    TripAdvancesComponent,
    ExpenseReceiptEditorComponent,
    CostCentersComponent,
    GlAccountRulesComponent,
    GlAccountRuleDrawerComponent,
    AccountSubledgerComponent,
    PartyStatementComponent,
  ],
  imports: [
    TabsModule,
    BrowserModule,
    AppRoutingModule,
    FormsModule,
    CommonModule,
    ReactiveFormsModule,
    BrowserAnimationsModule,
    DialogModule,
    ScrollingModule,
    CardSoftwareLibraryModule,
    PrimengModule,
    TranslateLocalizedPipe,
    TranslateWithFallbackPipe,
    MaskedAmountPipe,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useClass: CustomTranslateLoader,
      },
    }),
  ],
  exports: [TranslateWithFallbackPipe, TranslateLocalizedPipe, MaskedAmountPipe],
  providers: [
    { provide: LOGIN_AUTH_SERVICE, useClass: UserAuthenticationService },
    {
      provide: LOGIN_CONFIG,
      useValue: {
        domainAppName: 'Sierra Finance',
        contactEmail: 'it-support-sierra-textiles@card-software.com',
        redirectUrl: '/',
      },
    },
    provideHttpClient(withInterceptors([authInterceptor, errorInterceptor, siteInterceptor])),
    // MaskedAmountPipe formats through DecimalPipe, which Angular does not provide by default.
    DecimalPipe,
    { provide: SITE_API_BASE_URL, useValue: environment.proxyUrls.userMasterApi },
    provideAnimationsAsync(),
    // Day-first everywhere. Angular only consults this when `| date` is used with NO explicit format,
    // so it governs new code rather than retrofitting existing call sites.
    { provide: DATE_PIPE_DEFAULT_OPTIONS, useValue: { dateFormat: 'dd/MM/yyyy' } },
    providePrimeNG({
      theme: {
        preset: SierraTheme,
        options: {
          darkModeSelector: false,
        },
      },
      // PrimeNG's own default is 'mm/dd/yy' (US). Every p-datepicker without an explicit dateFormat
      // falls back to this, so setting it here is what actually makes the pickers day-first.
      // PrimeNG date tokens are not Angular's: 'yy' means a four-digit year.
      translation: { dateFormat: 'dd/mm/yy' },
    }),
    MessageService,
    ConfirmationService,
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
